import { z } from 'zod'
import {
  CANAUX_NOTIFICATION,
  DEVISE_PAR_DEFAUT,
  ESPACES_DRIVE,
  FREQUENCES,
  MODES_TRANSMISSION,
  NIVEAUX_ROLE,
  REGIMES_IMPOT,
  REGIMES_TVA,
  ROLES_TIERS,
  SOURCES_COMPTE,
  TYPES_CONTRAT,
  TYPES_ENTITE,
  TYPES_PERSONNE,
} from './domaine'
import { resoudreChemin } from './acheminement'
import { resoudreRacine } from './classement'
import { ibanValide, normaliserIban, sirenValide } from './identifiants'
import { motifExpediteur } from './consignes'
import { PRIVEE_IMMUABLE, refusTitulaire } from './espaces'

// Schémas d'entrée du référentiel, partagés par l'API et le MCP.
// Création : champs obligatoires et valeurs par défaut. Modification : tout est
// facultatif, null vide un champ ; l'entité de rattachement ne change pas.

const texte = z.string().trim().min(1)
const facultatif = <T extends z.ZodType>(schema: T) => schema.nullable().optional()
const id = z.uuid()
const jourCivil = z.iso.date()
const centimes = z.number().int().nonnegative()
const devise = z.string().regex(/^[A-Z]{3}$/, 'Code devise ISO (EUR, USD…)')

const siren = z.string().trim().refine(sirenValide, 'SIREN invalide (9 chiffres, clé de contrôle)')
const iban = z
  .string()
  .transform(normaliserIban)
  .refine(ibanValide, 'IBAN invalide (clé de contrôle)')
const cloture = z
  .string()
  .regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Clôture au format MM-JJ, par exemple 12-31')
const email = z.string().trim().toLowerCase().pipe(z.email('Adresse invalide'))
/** Chemin de rangement dans le Drive, relatif à sa racine, avec {AAAA}, {MM}, {AAAAMM}. */
const cheminDrive = z
  .string()
  .trim()
  .max(500)
  .superRefine((c, ctx) => {
    const r = resoudreChemin(c, '2026-01-01')
    if ('erreur' in r) ctx.addIssue({ code: 'custom', message: r.erreur })
  })

const racineClassement = z
  .string()
  .trim()
  .max(500)
  .superRefine((c, ctx) => {
    const r = resoudreRacine(c)
    if ('erreur' in r) ctx.addIssue({ code: 'custom', message: r.erreur })
  })

/** Id d'un Drive partagé Google (lettres, chiffres, tiret, tiret bas). */
const drivePartageId = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]{10,100}$/, 'Id de Drive partagé invalide.')
  // Jamais la clé d'un espace commun ('famille', 'flow_design') : prise par erreur pour un id,
  // elle se résoudrait vers le Drive commun lui-même (packages/integrations/src/google/drive.ts).
  .refine((v) => !(ESPACES_DRIVE as readonly string[]).includes(v), {
    message: 'Id de Drive partagé invalide (réservé à un espace commun).',
  })

// Entités
const champsEntite = {
  forme: facultatif(texte),
  siren: facultatif(siren),
  regimeTva: facultatif(z.enum(REGIMES_TVA)),
  regimeImpot: facultatif(z.enum(REGIMES_IMPOT)),
  cloture: facultatif(cloture),
  cabinetId: facultatif(id),
  plateformeAgreee: facultatif(texte),
  /** « mail » : les justificatifs partent à l'adresse de collecte du cabinet (Tiime). */
  transmission: z.enum(MODES_TRANSMISSION).optional(),
  adresseCollecte: facultatif(email),
  espaceDrive: z.enum(ESPACES_DRIVE).optional(),
  /**
   * Drive partagé propre à l'entité : l'emporte sur `espaceDrive`. Une entité privée sans lui
   * ne range pas (le rangement attend). Accepté seulement si le robot y a accès (vérifié avant
   * l'écriture, jamais dans ce schéma qui ne fait aucun appel réseau).
   */
  drivePartageId: facultatif(drivePartageId),
  /** Rangement dans le Drive, par rubrique ; vide : celui des « autres », sinon en attente. */
  cheminAchats: facultatif(cheminDrive),
  cheminVentes: facultatif(cheminDrive),
  cheminReleves: facultatif(cheminDrive),
  cheminAutres: facultatif(cheminDrive),
  /**
   * Racine du plan de classement dans le Drive partagé (dossier fixe, qui doit exister) : sous
   * elle, dossier permanent et exercices. Renseignée, elle remplace les quatre chemins.
   */
  racineClassement: facultatif(racineClassement),
  /** Autres façons dont les pièces la désignent : SIRET, TVA, anciens noms, adresses mail. */
  identifiants: z.array(texte.max(200)).max(50).optional(),
}
export const entiteCreation = z
  .object({
    nom: texte,
    type: z.enum(TYPES_ENTITE),
    ...champsEntite,
    /**
     * Espace privé : visible de ses seuls titulaires, administrateurs compris. Fixé à la
     * création, il ne change plus ensuite.
     */
    privee: z.boolean().optional(),
    /**
     * Titulaire de l'espace privé (rôle associé responsable), obligatoire avec lui seul.
     * Nullable : le formulaire de création (choix sans titulaire) envoie null, pas absent.
     */
    titulaireId: facultatif(id),
  })
  .superRefine((e, ctx) => {
    const refus = refusTitulaire(e)
    if (refus) ctx.addIssue({ code: 'custom', path: ['titulaireId'], message: refus })
  })
export const entiteModification = z
  .object({ nom: texte, type: z.enum(TYPES_ENTITE), ...champsEntite, archivee: z.boolean() })
  .partial()
  .extend({
    /** Immuable : refusé s'il est fourni. */
    privee: z.never({ error: PRIVEE_IMMUABLE }).optional(),
  })

// Personnes
export const personneCreation = z.object({
  nom: texte,
  email,
  type: z.enum(TYPES_PERSONNE).default('humain'),
  canalNotification: z.enum(CANAUX_NOTIFICATION).default('aucun'),
  slackId: facultatif(texte),
  /** Rattachement comme associé à une entité ; obligatoire pour un non-administrateur. */
  entiteId: facultatif(id),
  responsable: z.boolean().default(false),
})
export const personneModification = z
  .object({
    nom: texte,
    email,
    canalNotification: z.enum(CANAUX_NOTIFICATION),
    slackId: facultatif(texte),
  })
  .partial()

// Rôles
export const roleCreation = z.object({
  personneId: id,
  entiteId: id,
  niveau: z.enum(NIVEAUX_ROLE),
  responsable: z.boolean().default(false),
})
export const roleModification = z
  .object({ niveau: z.enum(NIVEAUX_ROLE), responsable: z.boolean() })
  .partial()

// Tiers
const champsTiers = {
  siren: facultatif(siren),
  iban: facultatif(iban),
  entiteParDefautId: facultatif(id),
}
export const tiersCreation = z.object({
  nom: texte,
  role: z.enum(ROLES_TIERS),
  ...champsTiers,
  libellesBancaires: z.array(texte).default([]),
  adressesMail: z.array(motifExpediteur).max(10).default([]),
})
export const tiersModification = z
  .object({
    nom: texte,
    role: z.enum(ROLES_TIERS),
    ...champsTiers,
    libellesBancaires: z.array(texte),
    adressesMail: z.array(motifExpediteur).max(10),
  })
  .partial()

// Biens
export const bienCreation = z.object({ nom: texte, adresse: texte, commune: texte, entiteId: id })
export const bienModification = z.object({ nom: texte, adresse: texte, commune: texte }).partial()

// Baux
const champsBail = {
  locataireId: facultatif(id),
  gestionnaireId: facultatif(id),
  loyerCts: facultatif(centimes),
  chargesCts: facultatif(centimes),
  indice: facultatif(texte),
  dateRevision: facultatif(jourCivil),
}
export const bailCreation = z.object({
  bienId: id,
  ...champsBail,
  devise: devise.default(DEVISE_PAR_DEFAUT),
})
export const bailModification = z.object({ ...champsBail, devise }).partial()

// Contrats
const champsContrat = {
  bienId: facultatif(id),
  montantCts: facultatif(centimes),
  canalFacture: facultatif(texte),
  /** Compte d'où part le prélèvement ; de la même entité que le contrat. */
  compteId: facultatif(id),
  /** Mots du libellé de relevé, pour retrouver ses débits. */
  motifReleve: facultatif(texte),
}
export const contratCreation = z.object({
  libelle: texte,
  tiersId: id,
  entiteId: id,
  type: z.enum(TYPES_CONTRAT),
  frequence: z.enum(FREQUENCES),
  ...champsContrat,
  devise: devise.default(DEVISE_PAR_DEFAUT),
})
export const contratModification = z
  .object({
    libelle: texte,
    tiersId: id,
    type: z.enum(TYPES_CONTRAT),
    frequence: z.enum(FREQUENCES),
    ...champsContrat,
    devise,
  })
  .partial()

// Comptes bancaires (la date de couverture du relevé est fixée par les imports)
export const compteCreation = z.object({
  libelle: texte,
  entiteId: id,
  banque: texte,
  source: z.enum(SOURCES_COMPTE).default('csv'),
})
export const compteModification = z
  .object({ libelle: texte, banque: texte, source: z.enum(SOURCES_COMPTE) })
  .partial()

export type EntiteCreation = z.infer<typeof entiteCreation>
export type EntiteModification = z.infer<typeof entiteModification>
export type PersonneCreation = z.infer<typeof personneCreation>
export type PersonneModification = z.infer<typeof personneModification>
export type RoleCreation = z.infer<typeof roleCreation>
export type RoleModification = z.infer<typeof roleModification>
export type TiersCreation = z.infer<typeof tiersCreation>
export type TiersModification = z.infer<typeof tiersModification>
export type BienCreation = z.infer<typeof bienCreation>
export type BienModification = z.infer<typeof bienModification>
export type BailCreation = z.infer<typeof bailCreation>
export type BailModification = z.infer<typeof bailModification>
export type ContratCreation = z.infer<typeof contratCreation>
export type ContratModification = z.infer<typeof contratModification>
export type CompteCreation = z.infer<typeof compteCreation>
export type CompteModification = z.infer<typeof compteModification>
